import { JWT_KEY } from "Auth/Models/AuthConstants";
import { Configuration } from "Infrastructure/Config/config";
import { ApiCallError } from "Infrastructure/Errors/ApiCallError";
import { AppRouting, getUrl } from "./UrlUtils";

export interface CustomFetchOptions extends RequestInit {
  baseUrl?: string;
  headers?: Record<string, string>;
  params?: Record<string, string>;
}

class HttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const normalizeParams = (params?: Record<string, string | number | boolean>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(params ?? {})
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, `${value}`]),
  );
};

let isRedirecting = false;

export const triggerSsoLogin = () => {
  localStorage.removeItem(JWT_KEY);
  if (!isRedirecting) {
    isRedirecting = true;
    processLogin(Configuration.getApiUrlZfp()).catch(() => {
      isRedirecting = false;
    });
  }
};

const processLogin = async (baseUrl: string | null) => {
  const redirectUrl = `${Configuration.getHostUrl()}${AppRouting.OAuthCallback.path()}`;
  const loginResponse = await fetch(getUrl(`${baseUrl}/v1/auth/login/zfp-sso`, { redirectUrl }), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (loginResponse.ok) {
    const { url } = await loginResponse.json();
    if (url) {
      window.location.replace(url as string);
    } else {
      console.error("Redirect URL not found in response", loginResponse);
      throw new HttpError(loginResponse.status, "Redirect URL not found in response");
    }
  } else {
    console.error("Redirect URL not found in response", loginResponse);
    throw new HttpError(loginResponse.status, `Login endpoint failed with status ${loginResponse.status}`);
  }
};

export async function customFetch<T>(
  request: {
    url: string;
    method: string;
    data?: BodyInit | object | null;
    params?: Record<string, string | number | boolean>;
    signal?: AbortSignal;
    headers?: Record<string, string>;
    responseType?: "blob" | "json";
  },
  options?: CustomFetchOptions,
): Promise<T> {
  const { baseUrl = Configuration.getApiUrlZfp(), headers: optionsHeaders = {}, params, ...restOptions } = options || {};

  const token = localStorage.getItem(JWT_KEY);
  const isFormData = request.data instanceof FormData;

  const body: BodyInit | undefined =
    request.data !== undefined && request.method !== "GET"
      ? isFormData
        ? (request.data as BodyInit)
        : JSON.stringify(request.data)
      : undefined;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `${token}` } : {}),
    ...request.headers,
    ...optionsHeaders,
  };

  // Orval emits a bare "multipart/form-data" header without the boundary, which makes the body unparsable;
  // for FormData the browser has to set the Content-Type (boundary included) itself.
  if (isFormData) {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === "content-type") delete headers[key];
    }
  }

  const response = await fetch(getUrl(`${baseUrl}${request.url}`, normalizeParams(request.params)), {
    method: request.method,
    headers,
    body,
    signal: request.signal,
    ...restOptions,
  });

  if (!response.ok) {
    if (response.status === 401) {
      triggerSsoLogin();
      throw new HttpError(401, "Unauthorized");
    }

    const errorText = await response.text();
    let errorPayload: any = {};

    try {
      errorPayload = JSON.parse(errorText);
    } catch (_e) {
      errorPayload = { message: errorText };
    }

    const apiError = new ApiCallError({
      data: null,
      error: errorPayload,
      status: response.status,
      args: request,
    });

    if (errorPayload && typeof errorPayload === "object") {
      Object.assign(apiError, errorPayload);
    }

    throw apiError;
  }

  if (response.status === 204) {
    return null as unknown as T;
  }

  if (request.responseType === "blob") {
    return response.blob() as unknown as Promise<T>;
  }

  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  return response.text() as unknown as T;
}
